
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function DemoHero() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeWord, setActiveWord] = useState(0);
  const words = ['Faster', 'Smarter', 'Better', 'Easier'];

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setActiveWord(prev => (prev + 1) % words.length);
    }, 2500);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-slate-950">
      {/* Premium Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/e0da85ba8371880d07cbcdc97d74b2ce.jpg')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/95 via-slate-950/85 to-slate-950" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-transparent to-slate-950/80" />
      </div>

      {/* Animated Grid Pattern */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `linear-gradient(rgba(251,191,36,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(251,191,36,0.3) 1px, transparent 1px)`,
          backgroundSize: '80px 80px',
        }}
      />

      {/* Floating Orbs */}
      <div className="absolute top-20 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-20 left-1/4 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

      <div className="max-w-7xl mx-auto px-6 relative z-10 w-full pt-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left column */}
          <div
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 mb-8">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-amber-400 font-semibold text-sm tracking-wide">
                Interactive Product Demo
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Experience
              <span className="block bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500 bg-clip-text text-transparent">
                MtaPlannerPro
              </span>
              <span className="inline-block mt-2 text-4xl md:text-5xl">
                Build Invoices{' '}
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">{words[activeWord]}</span>
                  <div className="absolute -bottom-2 left-0 right-0 h-1.5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"></div>
                </span>
              </span>
            </h1>

            <p className="text-xl text-slate-300 mb-10 leading-relaxed max-w-lg">
              See how MtaPlannerPro transforms job site photos into professional invoices with
              AI-powered pricing in under 60 seconds.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <a
                href="#live-demo"
                className="group inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full text-lg font-bold text-slate-900 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 shadow-2xl shadow-amber-500/30 hover:shadow-amber-500/50 transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105"
              >
                <i className="ri-play-circle-line text-2xl group-hover:scale-110 transition-transform" />
                Watch Live Demo
              </a>
              <Link
                to="/invoice"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full text-lg font-semibold text-white bg-white/5 hover:bg-white/10 border border-white/20 hover:border-amber-500/30 backdrop-blur-sm transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105"
              >
                <i className="ri-magic-line text-xl" />
                Try It Free
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  {[
                    'https://readdy.ai/api/search-image?query=professional%20australian%20tradesman%20portrait%20headshot%20confident%20smile%20wearing%20high%20visibility%20vest%20construction%20site%20background%20warm%20lighting&width=100&height=100&seq=tradie-avatar-1&orientation=squarish',
                    'https://readdy.ai/api/search-image?query=professional%20australian%20electrician%20portrait%20headshot%20friendly%20expression%20wearing%20work%20uniform%20clean%20background%20warm%20lighting&width=100&height=100&seq=tradie-avatar-2&orientation=squarish',
                    'https://readdy.ai/api/search-image?query=professional%20australian%20plumber%20portrait%20headshot%20confident%20smile%20wearing%20work%20clothes%20neutral%20background%20warm%20lighting&width=100&height=100&seq=tradie-avatar-3&orientation=squarish',
                    'https://readdy.ai/api/search-image?query=professional%20australian%20carpenter%20portrait%20headshot%20friendly%20expression%20wearing%20safety%20gear%20workshop%20background%20warm%20lighting&width=100&height=100&seq=tradie-avatar-4&orientation=squarish',
                    'https://readdy.ai/api/search-image?query=professional%20australian%20roofer%20portrait%20headshot%20confident%20smile%20wearing%20hard%20hat%20construction%20background%20warm%20lighting&width=100&height=100&seq=tradie-avatar-5&orientation=squarish',
                  ].map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt={`Tradie ${i + 1}`}
                      className="w-9 h-9 rounded-full border-2 border-slate-950 object-cover"
                    />
                  ))}
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">5,000+</p>
                  <p className="text-slate-400 text-xs">Active tradies</p>
                </div>
              </div>
              <div className="h-8 w-px bg-white/10" />
              <div>
                <div className="flex items-center gap-1 text-amber-400">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm" />
                  ))}
                </div>
                <p className="text-slate-400 text-xs">4.9/5 rating</p>
              </div>
            </div>
          </div>

          {/* Right column - Premium Dashboard Preview */}
          <div
            className={`transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}
          >
            <div className="relative">
              {/* Main Dashboard Card */}
              <div className="bg-slate-900/90 backdrop-blur-2xl rounded-3xl border border-white/10 shadow-2xl shadow-black/50 overflow-hidden">
                <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 border-b border-white/10 px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-amber-400" />
                    <div className="w-3 h-3 rounded-full bg-emerald-400" />
                    <span className="ml-3 text-slate-400 text-xs font-mono">
                      mtaplannerpro.com/dashboard
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="rounded-2xl overflow-hidden" style={{ height: '380px' }}>
                    <img
                      src="https://readdy.ai/api/search-image?query=premium%20construction%20project%20management%20software%20dashboard%20interface%20showing%20real%20time%20analytics%20invoices%20material%20costs%20profit%20margins%20beautiful%20data%20visualization%20charts%20amber%20orange%20accent%20colors%20on%20dark%20slate%20background%20modern%20glassmorphism%20UI%20design%20professional%20SaaS%20application%20screenshot%20high%20fidelity%20mockup&width=800&height=500&seq=demo-dashboard-premium-v3&orientation=landscape"
                      alt="MtaPlannerPro Dashboard"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              {/* Floating Success Card */}
              <div className="absolute -left-8 top-1/4 animate-bounce" style={{ animationDuration: '3s' }}>
                <div className="bg-slate-900/95 backdrop-blur-xl rounded-2xl px-5 py-4 border border-emerald-500/30 shadow-2xl shadow-emerald-500/10">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30">
                      <i className="ri-check-double-line text-white text-xl" />
                    </div>
                    <div>
                      <p className="text-white font-bold">Quote Accepted!</p>
                      <p className="text-emerald-400 text-sm font-medium">$8,450 • HVAC Install</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Timer Card */}
              <div
                className="absolute -right-6 bottom-1/3 animate-bounce"
                style={{ animationDuration: '3.5s', animationDelay: '1s' }}
              >
                <div className="bg-slate-900/95 backdrop-blur-xl rounded-2xl px-5 py-4 border border-amber-500/30 shadow-2xl shadow-amber-500/10">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                      <i className="ri-timer-flash-line text-white text-xl" />
                    </div>
                    <div>
                      <p className="text-white font-bold">60 Seconds</p>
                      <p className="text-amber-400 text-sm font-medium">Average quote time</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Badge */}
              <div className="absolute -top-4 right-8">
                <div className="bg-gradient-to-r from-violet-500 to-purple-600 rounded-full px-4 py-2 shadow-lg shadow-violet-500/30">
                  <div className="flex items-center gap-2">
                    <i className="ri-sparkling-2-fill text-white" />
                    <span className="text-white text-sm font-bold">AI Powered</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <a href="#live-demo" className="flex flex-col items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors cursor-pointer">
          <span className="text-xs font-medium uppercase tracking-wider">Scroll to Demo</span>
          <i className="ri-arrow-down-line text-xl" />
        </a>
      </div>
    </section>
  );
}
